var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightIcon, BoltIcon, CheckCircleIcon, ChevronRightIcon, PhoneIcon, ShieldCheckIcon, TruckIcon, } from '@heroicons/react/24/outline';
import { logAdEvent } from '../services/adsService';
import { useActiveHoliday } from '../hooks/useActiveHoliday';
import HolidayGreetingBadge from './HolidayGreetingBadge';
import { APP_LOGO_SOURCES, withLogoAssetVersion } from '../utils/logoAssetVersion';
import { getSupportPhone } from '../utils/envUtils';
import { trackEvent } from '../utils/analytics';
import { formatPhoneNumber } from '../helpers/formatPhoneNumber';
var HIGH_QUALITY_INTRO_LOGO_SRC = APP_LOGO_SOURCES.desktop.webp;
var OPTIMIZED_INTRO_LOGO_SRC = APP_LOGO_SOURCES.desktop.webp;
var PROMO_LOGO_ALT_TEXT = 'Secure gated truck parking lot operated by Truck Parking Hub';
var LOW_BANDWIDTH_QUERY_VALUES = new Set(['low', 'optimized', 'lite']);
var HIGH_BANDWIDTH_QUERY_VALUES = new Set(['high', 'full']);
var ADAPTIVE_LOGO_SOURCE_MAP = {
    '/tph_logo.png': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph_logo_ui.webp': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-mobile.png': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-mobile.webp': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-desktop.png': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-desktop.webp': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-mobile.avif': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
    '/tph-logo-desktop.avif': { optimized: APP_LOGO_SOURCES.desktop.webp, high: APP_LOGO_SOURCES.desktop.webp },
};
var YARD_V2_GREEN = '#54B948';
var YARD_V2_CYAN = '#7FD0E0';
var YARD_V2_GOLD = '#B8860B';
var getCompactSlideVisual = function (slide) {
    if (slide.isIntro) {
        return {
            icon: TruckIcon,
            tone: YARD_V2_CYAN,
            kicker: 'Parking location',
            title: slide.title || 'Your parking location',
            sub: slide.description || '',
            cta: null,
        };
    }
    if (slide.isPromo) {
        var discount = slide.promoDiscountLabel || '15%';
        return {
            icon: BoltIcon,
            tone: YARD_V2_GOLD,
            kicker: "Save ".concat(discount),
            title: slide.title || 'Limited-time parking promo',
            sub: slide.description || 'Available now - limited-time promo pricing.',
            cta: slide.ctaLabel || 'Book Now',
        };
    }
    var title = slide.title || 'Secure truck parking';
    var isSecuritySlide = /secure|gated|camera|surveillance/i.test("".concat(title, " ").concat(slide.description || ''));
    return {
        icon: isSecuritySlide ? ShieldCheckIcon : CheckCircleIcon,
        tone: isSecuritySlide ? YARD_V2_CYAN : YARD_V2_GREEN,
        kicker: slide.learnMore ? 'Featured' : 'Open & ready',
        title: title,
        sub: slide.description || '',
        cta: slide.learnMore ? 'Learn more' : (slide.ctaLabel || (slide.locationId ? 'Book Now' : null)),
    };
};
var getBandwidthModeOverride = function () {
    if (typeof window === 'undefined')
        return null;
    try {
        var params = new URLSearchParams(window.location.search);
        var queryMode = String(params.get('bw') || '').toLowerCase();
        if (LOW_BANDWIDTH_QUERY_VALUES.has(queryMode))
            return true;
        if (HIGH_BANDWIDTH_QUERY_VALUES.has(queryMode))
            return false;
    }
    catch (_error) {
        // no-op
    }
    try {
        var localMode = String(window.localStorage.getItem('rst-bandwidth-mode') || '').toLowerCase();
        if (LOW_BANDWIDTH_QUERY_VALUES.has(localMode))
            return true;
        if (HIGH_BANDWIDTH_QUERY_VALUES.has(localMode))
            return false;
    }
    catch (_error) {
        // no-op
    }
    return null;
};
var resolveAdaptiveSlideImageSrc = function (src, preferOptimizedAssets) {
    var normalizedSrc = String(src || '').trim();
    if (!normalizedSrc)
        return normalizedSrc;
    var mapped = ADAPTIVE_LOGO_SOURCE_MAP[normalizedSrc];
    if (!mapped)
        return normalizedSrc;
    return withLogoAssetVersion(preferOptimizedAssets ? mapped.optimized : mapped.high);
};
var AdSlider = function (_a) {
    var items = _a.items, _b = _a.intervalMs, intervalMs = _b === void 0 ? 3000 : _b, onCtaClick = _a.onCtaClick, locationAddress = _a.locationAddress, _c = _a.compact, compact = _c === void 0 ? false : _c;
    var activeHoliday = useActiveHoliday();
    var _d = useState(true), preferOptimizedAssets = _d[0], setPreferOptimizedAssets = _d[1];
    useEffect(function () {
        var override = getBandwidthModeOverride();
        setPreferOptimizedAssets(override !== null ? override : true);
    }, []);
    var slides = useMemo(function () {
        var base = Array.isArray(items) ? items : [];
        var adaptedBase = base.map(function (item) { return (__assign(__assign({}, item), { alt: item.isPromo && ADAPTIVE_LOGO_SOURCE_MAP[item.imageSrc]
                ? PROMO_LOGO_ALT_TEXT
                : item.alt, imageSrc: resolveAdaptiveSlideImageSrc(item.imageSrc, preferOptimizedAssets) })); });
        if (locationAddress) {
            var intro = {
                id: 'intro-location',
                imageSrc: preferOptimizedAssets ? OPTIMIZED_INTRO_LOGO_SRC : HIGH_QUALITY_INTRO_LOGO_SRC,
                alt: 'Your parking location',
                title: 'Your parking location',
                description: locationAddress,
                isIntro: true,
            };
            return __spreadArray([intro], adaptedBase, true);
        }
        return adaptedBase;
    }, [items, locationAddress, preferOptimizedAssets]);
    var _e = useState(0), activeIndex = _e[0], setActiveIndex = _e[1];
    var _f = useState(false), isPaused = _f[0], setIsPaused = _f[1];
    var _g = useState(false), prefersReducedMotion = _g[0], setPrefersReducedMotion = _g[1];
    var _h = useState(null), slideDirection = _h[0], setSlideDirection = _h[1];
    var _j = useState(true), isInitialSlideLockActive = _j[0], setIsInitialSlideLockActive = _j[1];
    var intervalRef = useRef(null);
    var seenImpressionsRef = useRef(new Set());
    var touchStartX = useRef(null);
    var prevIndexRef = useRef(0);
    var supportPhone = getSupportPhone();
    var supportPhoneHref = supportPhone ? "tel:".concat(String(supportPhone).replace(/[^0-9+]/g, '')) : undefined;
    var supportPhoneLabel = supportPhone ? (formatPhoneNumber(String(supportPhone)) || supportPhone) : '';
    var handleSupportCallClick = function () {
        trackEvent('phone_contact_click', { source: 'ad_slider', action: 'call' });
    };
    var handleCtaClick = function (slide) {
        logAdEvent('book_now_click', {
            source: 'ad_slider',
            adId: slide.id,
            locationId: slide.locationId,
            isPromo: !!slide.isPromo,
            ctaLabel: slide.ctaLabel || 'Book Now'
        });
        onCtaClick === null || onCtaClick === void 0 ? void 0 : onCtaClick(slide);
    };
    useEffect(function () {
        var rafId = null;
        var timeoutId = null;
        // Keep first paint focused on the active slide; unlock neighbors immediately after.
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            rafId = window.requestAnimationFrame(function () { return setIsInitialSlideLockActive(false); });
        }
        else {
            timeoutId = window.setTimeout(function () { return setIsInitialSlideLockActive(false); }, 0);
        }
        return function () {
            if (rafId !== null && typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(rafId);
            }
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);
    // Respect the user's reduced-motion preference: pause auto-advance (WCAG 2.2.2).
    // Manual nav (dots, arrows, swipe) still works.
    useEffect(function () {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return undefined;
        var mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        var updatePreference = function () { return setPrefersReducedMotion(mql.matches); };
        updatePreference();
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', updatePreference);
            return function () { return mql.removeEventListener('change', updatePreference); };
        }
        mql.addListener(updatePreference);
        return function () { return mql.removeListener(updatePreference); };
    }, []);
    var slideIndexesToRender = useMemo(function () {
        var total = slides.length;
        if (isInitialSlideLockActive) {
            return new Set([activeIndex]);
        }
        if (total <= 3) {
            return new Set(slides.map(function (_, index) { return index; }));
        }
        var previousIndex = prevIndexRef.current;
        var prevNeighbor = (activeIndex - 1 + total) % total;
        var nextNeighbor = (activeIndex + 1) % total;
        return new Set([activeIndex, previousIndex, prevNeighbor, nextNeighbor]);
    }, [activeIndex, slides, slideDirection, isInitialSlideLockActive]);
    var getImageLoadingProps = function (index) {
        if (index === activeIndex) {
            return { loading: 'eager', fetchpriority: 'high' };
        }
        if (isInitialSlideLockActive) {
            return { loading: 'lazy', fetchpriority: 'low' };
        }
        // Keep the immediate next slide eager so carousel-driven LCP candidates
        // are not blocked behind lazy-loading when auto-rotation advances.
        var immediateNextIndex = slides.length > 1 ? (activeIndex + 1) % slides.length : -1;
        if (index === immediateNextIndex) {
            return { loading: 'eager', fetchpriority: 'auto' };
        }
        return { loading: 'lazy', fetchpriority: 'low' };
    };
    // Navigate to slide with direction animation
    var goToSlide = function (newIndex, direction) {
        setSlideDirection(direction);
        prevIndexRef.current = activeIndex;
        setActiveIndex(newIndex);
        // Reset direction after animation
        setTimeout(function () { return setSlideDirection(null); }, 500);
    };
    // Touch swipe handlers
    var handleTouchStart = function (e) {
        touchStartX.current = e.touches[0].clientX;
        setIsPaused(true);
    };
    var handleTouchEnd = function (e) {
        if (touchStartX.current === null)
            return;
        var touchEndX = e.changedTouches[0].clientX;
        var diff = touchStartX.current - touchEndX;
        var minSwipeDistance = 50;
        if (Math.abs(diff) > minSwipeDistance) {
            if (diff > 0) {
                // Swiped left → next slide
                goToSlide((activeIndex + 1) % slides.length, 'left');
            }
            else {
                // Swiped right → previous slide
                goToSlide((activeIndex - 1 + slides.length) % slides.length, 'right');
            }
        }
        touchStartX.current = null;
        // Resume auto-rotation after a delay
        setTimeout(function () { return setIsPaused(false); }, 3000);
    };
    // Track impressions once per slide id per component lifecycle (skip intro)
    useEffect(function () {
        if (slides.length === 0)
            return;
        var current = slides[activeIndex];
        if (current && !current.isIntro && !seenImpressionsRef.current.has(current.id)) {
            seenImpressionsRef.current.add(current.id);
            logAdEvent('ad_impression', {
                adId: current.id,
                isPromo: !!current.isPromo,
                locationId: current.locationId || undefined,
            });
        }
    }, [activeIndex, slides]);
    useEffect(function () {
        var _a;
        if (slides.length <= 1 || isPaused || prefersReducedMotion) {
            return;
        }
        var tick = function () {
            setSlideDirection('left');
            prevIndexRef.current = activeIndex;
            setActiveIndex(function (prev) { return (prev + 1) % slides.length; });
            setTimeout(function () { return setSlideDirection(null); }, 500);
        };
        var introDurationMs = 6000;
        var currentDuration = ((_a = slides[activeIndex]) === null || _a === void 0 ? void 0 : _a.isIntro) ? introDurationMs : intervalMs;
        intervalRef.current = window.setInterval(tick, Math.max(1500, currentDuration));
        return function () {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
            }
        };
    }, [slides.length, intervalMs, isPaused, activeIndex, slides, prefersReducedMotion]);
    // Helper to get slide animation classes
    var getSlideClasses = function (index) {
        var isActive = index === activeIndex;
        var wasPrevious = index === prevIndexRef.current;
        if (isActive) {
            return 'opacity-100 translate-x-0 pointer-events-auto z-10';
        }
        if (wasPrevious && slideDirection) {
            // Exiting slide - slide out in direction
            return slideDirection === 'left'
                ? 'opacity-0 -translate-x-full pointer-events-none z-0'
                : 'opacity-0 translate-x-full pointer-events-none z-0';
        }
        return 'opacity-0 pointer-events-none z-0';
    };
    // Early return AFTER all hooks
    if (slides.length === 0) {
        return null;
    }
    return (_jsx("div", __assign({ className: "w-full touch-pan-y", "aria-live": "polite", role: "region", "aria-label": "Promotional offers", onMouseEnter: function () { return setIsPaused(true); }, onMouseLeave: function () { return setIsPaused(false); }, onFocusCapture: function () { return setIsPaused(true); }, onBlurCapture: function () { return setIsPaused(false); }, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, tabIndex: 0 }, { children: compact ? (
        /* Yard v2 compact layout: status/rating row + support button */
        _jsxs("div", __assign({ className: "relative flex w-full flex-col overflow-hidden rounded-[18px] border border-white/10 bg-[#142336] px-[18px] py-[18px] md:min-h-[230px] md:px-[22px] md:py-[22px]", "data-testid": "adslider-yard-v2" }, { children: [_jsx("div", __assign({ className: "relative min-h-[104px] flex-1 pr-0 md:min-h-[128px] md:pr-24" }, { children: slides.map(function (slide, index) { return (slideIndexesToRender.has(index) ? (_jsx("div", __assign({ className: "absolute inset-0 flex items-stretch transition-all duration-500 ease-out ".concat(getSlideClasses(index)), "aria-hidden": index === activeIndex ? 'false' : 'true' }, { children: (function () {
                            var visual = getCompactSlideVisual(slide);
                            var ctaHref = slide.learnMore && slide.href
                                ? (function () {
                                    var url = new URL(slide.href.startsWith('http') ? slide.href : (window.location.origin + slide.href));
                                    if (slide.lang)
                                        url.searchParams.set('lang', slide.lang);
                                    return url.toString().replace(window.location.origin, '');
                                })()
                                : null;
                            return (_jsxs("div", __assign({ className: "flex min-h-[104px] min-w-0 flex-1 flex-col justify-between text-left md:min-h-[128px]", "data-testid": "adslider-compact-copy" }, { children: [_jsxs("div", __assign({ className: "mb-3 flex min-w-0 items-start justify-between gap-2 md:pr-0" }, { children: [_jsxs("div", __assign({ className: "flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5" }, { children: [_jsxs("span", __assign({ className: "inline-flex h-7 max-w-full items-center rounded-md border px-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]", "data-testid": "adslider-compact-status", style: { backgroundColor: "".concat(visual.tone, "1f"), borderColor: "".concat(visual.tone, "35"), color: visual.tone } }, { children: [_jsx("span", { className: "mr-1.5 h-1.5 w-1.5 rounded-full", style: { backgroundColor: visual.tone }, "aria-hidden": "true" }), _jsx("span", __assign({ className: "truncate" }, { children: visual.kicker }))] })), _jsxs("span", __assign({ className: "inline-flex h-7 items-center gap-1 font-mono text-[12px] font-semibold text-[#FFC846]", "data-testid": "adslider-compact-rating", "aria-label": "Rating 5.0" }, { children: [_jsx("span", __assign({ "aria-hidden": "true" }, { children: "\u2605" })), _jsx("span", { children: "5.0" })] }))] })), supportPhoneHref && supportPhoneLabel && (_jsxs("a", __assign({ href: supportPhoneHref, onClick: handleSupportCallClick, className: "inline-flex h-7 shrink-0 items-center gap-1 self-start text-right font-mono text-[12px] font-semibold text-[#FFC846] underline-offset-4 transition-colors hover:text-[#F3EEDF] hover:underline", "data-testid": "adslider-compact-support-link", "aria-label": "Call support at ".concat(supportPhoneLabel), title: "Call support" }, { children: [_jsx(PhoneIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), _jsx("span", { children: supportPhoneLabel })] })))] })), _jsxs("div", __assign({ className: "min-h-[52px]" }, { children: [_jsx("div", __assign({ className: "font-semibold leading-[1.12] tracking-[-0.01em] text-[#F3EEDF] text-[19px] md:text-[22px]" }, { children: visual.title })), (visual.sub || visual.cta) && (_jsxs("div", __assign({ className: "mt-1.5 font-mono text-xs text-[#F3EEDF]/55" }, { children: [visual.sub && (_jsx("span", { children: visual.sub })), visual.cta && (_jsxs(_Fragment, { children: [visual.sub && _jsx("span", __assign({ className: "mx-2 text-[#F3EEDF]/28", "aria-hidden": "true" }, { children: "|" })), ctaHref ? (_jsxs("a", __assign({ href: ctaHref, className: "inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-[#7FD0E0] underline-offset-4 transition-colors hover:text-[#A8E8F3] hover:underline", "aria-label": slide.title ? "Learn more about ".concat(slide.title) : undefined, onClick: function () { return logAdEvent('ad_learn_more', { adId: slide.id, href: slide.href, lang: slide.lang }); } }, { children: [visual.cta, _jsx(ArrowRightIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" })] }))) : onCtaClick && (slide.isPromo || slide.locationId) ? (_jsxs("button", __assign({ type: "button", "data-testid": "adslider-cta", className: "inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-[#7FD0E0] underline-offset-4 transition-colors hover:text-[#A8E8F3] hover:underline", onClick: function () { return handleCtaClick(slide); } }, { children: [visual.cta, _jsx(ArrowRightIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" })] }))) : null] }))] }))), slide.isIntro && activeHoliday && index === activeIndex && (_jsx(HolidayGreetingBadge, { holiday: activeHoliday, size: "compact", className: "mt-2" }))] })), _jsx("div", { className: "min-h-[36px]" })] })));
                        })() }), slide.id)) : null); }) })), _jsxs("div", __assign({ className: "mt-5 flex items-center justify-between" }, { children: [_jsx("div", __assign({ className: "flex items-center gap-0.5" }, { children: slides.map(function (s, i) { return (_jsx("button", __assign({ type: "button", "aria-label": "Slide ".concat(i + 1), "aria-current": i === activeIndex ? 'true' : undefined, className: "group flex items-center justify-center border-0 bg-transparent px-1 py-2.5", onClick: function () {
                                    setIsPaused(true);
                                    prevIndexRef.current = activeIndex;
                                    setActiveIndex(i);
                                } }, { children: _jsx("span", { className: "block h-1.5 rounded-full transition-all ".concat(i === activeIndex ? 'w-[18px] bg-[#FFC846]' : 'w-1.5 bg-white/20 group-hover:bg-white/40') }) }), s.id)); }) })), slides.length > 1 && (_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [_jsx("button", __assign({ type: "button", "aria-label": "Previous slide", className: "flex h-[30px] w-[30px] rotate-180 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-[#F3EEDF] transition-colors hover:bg-white/10", onClick: function () {
                                        setIsPaused(true);
                                        goToSlide((activeIndex - 1 + slides.length) % slides.length, 'right');
                                    } }, { children: _jsx(ChevronRightIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" }) })), _jsx("button", __assign({ type: "button", "aria-label": "Next slide", className: "flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-[#F3EEDF] transition-colors hover:bg-white/10", onClick: function () {
                                        setIsPaused(true);
                                        goToSlide((activeIndex + 1) % slides.length, 'left');
                                    } }, { children: _jsx(ChevronRightIcon, { className: "h-3.5 w-3.5", "aria-hidden": "true" }) }))] })))] }))] }))) : (
        /* Standard vertical layout */
        _jsxs("div", __assign({ className: "relative w-full overflow-hidden rounded-md" }, { children: [_jsx("div", __assign({ className: "relative h-40 md:h-48 lg:h-56" }, { children: slides.map(function (slide, index) { return (slideIndexesToRender.has(index) ? (_jsx("div", __assign({ className: "absolute inset-0 flex flex-col items-center justify-center gap-2 pb-2 md:pb-3 lg:pb-4 transition-all duration-500 ease-out ".concat(getSlideClasses(index)), "aria-hidden": index === activeIndex ? 'false' : 'true' }, { children: _jsx("div", __assign({ className: "flex flex-col items-center gap-3" }, { children: slide.isIntro ? (_jsxs(_Fragment, { children: [_jsx("img", __assign({ src: slide.imageSrc, alt: slide.alt, width: "320", height: "320", className: "h-16 sm:h-20 md:h-32 lg:h-36 object-contain frost-logo", decoding: slide.isIntro ? undefined : 'async' }, getImageLoadingProps(index))), _jsxs("div", __assign({ className: "flex flex-col items-center text-center gap-1 px-3 sm:px-4 w-full" }, { children: [_jsx("div", __assign({ className: "text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-[--lamborghini-green]" }, { children: "Your parking location" })), _jsx("div", __assign({ className: "text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 dark:text-[#efeff1] opacity-90 w-full text-center leading-snug", title: slide.description || '' }, { children: _jsx("span", __assign({ className: "line-clamp-2 sm:line-clamp-1" }, { children: slide.description })) })), activeHoliday && index === activeIndex && (_jsx(HolidayGreetingBadge, { holiday: activeHoliday, mirroredIcons: true, className: "mt-1 sm:mt-2" }))] }))] })) : (_jsxs(_Fragment, { children: [_jsx("img", __assign({ src: slide.imageSrc, alt: slide.alt, width: "320", height: "320", className: "h-20 md:h-32 lg:h-36 object-contain frost-logo".concat(slide.rounded ? ' rounded-2xl' : ''), decoding: slide.isIntro ? undefined : 'async' }, getImageLoadingProps(index))), (slide.title || slide.description || (onCtaClick && (slide.isPromo || slide.locationId))) && (_jsxs("div", __assign({ className: "text-center" }, { children: [slide.title && (_jsx("div", __assign({ className: "text-lg md:text-xl lg:text-2xl font-semibold text-[--lamborghini-green]" }, { children: slide.title }))), slide.isPromo ? (_jsxs("div", __assign({ className: "mt-1 flex flex-wrap items-center justify-center gap-3" }, { children: [onCtaClick && (_jsx("button", __assign({ type: "button", "data-testid": "adslider-cta", className: "px-3 py-1.5 rounded-md bg-[--lamborghini-blue] text-white text-xs md:text-sm hover:bg-[--lamborghini-blue]/90 transition-colors", onClick: function () {
                                                            logAdEvent('book_now_click', {
                                                                source: 'ad_slider',
                                                                adId: slide.id,
                                                                locationId: slide.locationId,
                                                                isPromo: true,
                                                                ctaLabel: slide.ctaLabel || 'Book Now'
                                                            });
                                                            onCtaClick(slide);
                                                        } }, { children: slide.ctaLabel || 'Book Now' }))), _jsx("span", __assign({ className: "px-2 py-0.5 rounded-md text-[var(--accent-500)] text-xs md:text-sm font-semibold" }, { children: "Save ".concat(slide.promoDiscountLabel || '15%', " on daily rates!") }))] }))) : ((slide.description || (onCtaClick && slide.locationId) || (slide.learnMore && slide.href)) && (_jsxs("div", __assign({ className: "mt-1 flex flex-wrap items-center justify-center gap-3" }, { children: [slide.description && (_jsx("span", __assign({ className: "text-sm md:text-base lg:text-lg text-gray-700 dark:text-[#efeff1] opacity-90" }, { children: slide.description }))), onCtaClick && slide.locationId && (_jsx("button", __assign({ type: "button", "data-testid": "adslider-cta", className: "px-3 py-1.5 rounded-md border-2 border-[--lamborghini-blue] text-xs md:text-sm text-[--lamborghini-blue] hover:bg-[--lamborghini-blue]/10 transition-colors", onClick: function () {
                                                            logAdEvent('book_now_click', {
                                                                source: 'ad_slider',
                                                                adId: slide.id,
                                                                locationId: slide.locationId,
                                                                isPromo: false,
                                                                ctaLabel: slide.ctaLabel || 'Book Now'
                                                            });
                                                            onCtaClick(slide);
                                                        } }, { children: slide.ctaLabel || 'Book Now' }))), slide.learnMore && slide.href && (_jsx("a", __assign({ href: (function () {
                                                            var url = new URL(slide.href.startsWith('http') ? slide.href : (window.location.origin + slide.href));
                                                            if (slide.lang)
                                                                url.searchParams.set('lang', slide.lang);
                                                            return url.toString().replace(window.location.origin, '');
                                                        })(), className: "text-[--lamborghini-blue] underline text-sm", "aria-label": slide.title ? "Learn more about ".concat(slide.title) : undefined, onClick: function () { return logAdEvent('ad_learn_more', { adId: slide.id, href: slide.href, lang: slide.lang }); } }, { children: slide.lang === 'ru' ? 'Подробнее' : slide.lang === 'es' ? 'Más info' : 'Learn more' })))] }))))] })))] })) })) }), slide.id)) : null); }) })), slides.length > 1 && (_jsx("div", __assign({ className: "flex items-center justify-center gap-2 mt-4 md:-mt-2 pb-1" }, { children: slides.map(function (s, i) { return (_jsx("button", { "aria-label": "Go to slide ".concat(i + 1), className: "w-3 h-3 rounded-full transition-colors ".concat(i === activeIndex ? 'bg-gray-900 dark:bg-white' : 'bg-gray-400/60 dark:bg-white/40 hover:bg-gray-600 dark:hover:bg-white/70'), onClick: function () { setIsPaused(true); setActiveIndex(i); } }, s.id)); }) })))] }))) })));
};
export default AdSlider;
